import React from 'react';
import {
  Button,
  Box,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ImportMap from './Import'; // <--- Import the new file

function Menu() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openResetDialog, setOpenResetDialog] = React.useState(false);
  const mapActive = localStorage.getItem('map-active');
  const [useStarterMap, setUseStarterMap] = React.useState(true);

  const navigateToCanvas = () => {
    navigate('/create');
  };

  const handleNewMapConfirmed = async () => {
    localStorage.removeItem('map-active');
    if (useStarterMap) {
      try {
        const response = await fetch('/startermap.json');
        if (!response.ok) throw new Error('Failed to fetch starter map');
        const starterData = await response.json();
        localStorage.setItem('map-active', JSON.stringify(starterData));
      } catch (error) {
        console.error('Error loading starter map:', error);
      }
    }

    setOpenResetDialog(false);
    navigateToCanvas();
  };

  const handleContinueEditing = () => {
    navigateToCanvas();
  };

  // Common button styles (shared across all buttons)
  const buttonStyles = {
    fontFamily: "'Germania One', cursive",
    fontSize: { xs: '1rem', sm: '1.5rem' }, // Smaller text on mobile
    padding: { xs: '8px 16px', sm: '15px 30px' }, // Adjusted padding
    width: { xs: '180px', sm: '250px' }, // Ensures proper button width
    backgroundColor: "rgba(23, 23, 23, 0.7)",
    color: theme.palette.text.primary,
    border: '1px solid',
    borderColor: theme.palette.primary.main,
    transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
    "&:hover": {
      backgroundColor: "rgba(23, 23, 23, 0.9)",
      boxShadow: `0 0 8px ${theme.palette.primary.main}`,
    },
  };

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
        padding: { xs: 1, sm: 2 },
      }}
    >
      {/* Background Animation */}
      <motion.div
        initial={{ x: '-10%', scale: 1.2 }}
        animate={{
          x: ['-10%', '10%', '-10%'],
          transition: {
            duration: 25,
            repeat: Infinity,
            repeatType: 'mirror',
            ease: 'linear',
          },
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '110%',
          height: '110%',
          backgroundImage: "url('/Map_1.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(5px)',
        }}
      />

      {/* Title */}
      <Typography
        variant="h2"
        sx={{
          fontFamily: "'Germania One', cursive",
          color: theme.palette.primary.main,
          WebkitTextStroke: '1px black',
          fontWeight: 'bold',
          zIndex: 1,
          fontSize: { xs: '1.5rem', sm: '3rem', md: '4rem' },
          marginBottom: { xs: 1, sm: 3 },
        }}
      >
        ReconQuest Map Maker
      </Typography>

      {/* Buttons Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column" }, // Stacks elements
          gap: { xs: 1, sm: 2 },
          alignItems: "center",
          zIndex: 1,
          width: "90%", // prevent overflow
          maxWidth: "400px",
        }}
      >
        {mapActive ? (
          <>
            <Button variant="contained" sx={buttonStyles} onClick={handleContinueEditing}>
              Continue Editing
            </Button>
            <Button variant="contained" sx={buttonStyles} onClick={() => setOpenResetDialog(true)}>
              New Map
            </Button>
            {/* Toggle for "Use Starter Map" */}
            <FormControlLabel
              sx={{
                color: theme.palette.text.primary,
                textAlign: "center",
                "& .MuiFormControlLabel-label": {
                  fontSize: { xs: "0.8rem", sm: "1rem" },
                },
              }}
              control={
                <Switch
                  checked={useStarterMap}
                  onChange={(e) => setUseStarterMap(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Use Starter Map"
            />
          </>
        ) : (
          <>
            <Button variant="contained" sx={buttonStyles} onClick={() => setOpenResetDialog(true)}>
              New Map
            </Button>
            {/* Toggle for "Use Starter Map" */}
            <FormControlLabel
              sx={{
                color: theme.palette.text.primary,
                "& .MuiFormControlLabel-label": {
                  fontSize: { xs: "0.8rem", sm: "1rem" },
                },
              }}
              control={
                <Switch
                  checked={useStarterMap}
                  onChange={(e) => setUseStarterMap(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Use Starter Map"
            />
          </>
        )}

        {/* Import Map (Now a separate component) */}
        <ImportMap buttonStyles={buttonStyles} />
      </Box>

      {/* Confirmation Dialog for New Map */}
      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
        <DialogTitle>Confirm New Map</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to start a new map? This will erase your current progress.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleNewMapConfirmed} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Menu;